<template>
  <b-modal
    ref="jobModal"
    id="jobDescriptionModal"
    size="lg"
    modal-class="job-description-modal"
    hide-footer
    hide-
    @hidden="onHidden"
  >
    <!-- Job Description Content -->
     <div :class="{'jd-content': loading || errorMessage || !hasValidJobData}">
        <Loader v-if="loading" />
        <div v-else-if="errorMessage" class="error-message description pad-1">
          {{ errorMessage }}
        </div>
        <div v-else-if="!hasValidJobData" class="error-message description pad-1">
          {{ validationErrorMessage }}
        </div>
        <div class="job-content" v-else-if="hasValidJobData">
          <div class="job-header pad-1" style="border-bottom: 1px solid #787878; height: 180px;">
            <h3 class="mb-2" style="font-weight: 600">{{ currentJob.companyName }}</h3>
            <h5 style="height: 90px;" v-html="highlightText(currentJob.job_title, 'job_title')"></h5>
            <p>求人更新日 {{ jobDate }}</p>
          </div>  
          <div v-html="highlightText(currentJob.job_description, 'job_description')" class="description pad-1"></div>
        </div>
     </div>

    <div class="modal-footer scout-footer w-100" v-if="(!loading && !errorMessage && hasValidJobData)">
      <div class="scout-button-section">
          <b-button
            @click="handleScout"
            :class="buttonClass"
            :disabled="isScouted"
            style="width: 100%;"
          >
            {{buttonText}}
          </b-button>
          <p :class="isScouted ? 'scouted-hint' : 'scout-hint'">
            {{ scoutHintText }}
          </p>
        </div>
    </div>
    
    <div class="modal-footer navigation-footer w-100" v-if="(!loading && !errorMessage && hasValidJobData)">
      <div class="navigation-buttons">
        <span v-if="isInitialScout"
          >{{ currentIndex + 1 }}/{{ totalJobs }} 件表示</span
        >
        <button 
          v-else
          @click="hide" 
          class="close-button"
          title="閉じる"
        >
          <i class="fas fa-times"></i>
        </button>
        <div class="navigation-toggler" style="">
          <span
            v-if="currentIndex > 0"
            style="color: #000000; font-weight: 500;"
          >
            <span 
              @click="handlePrev"
              class="prev-link"
              style="cursor: pointer; color: #000000; font-weight: 500;"
            >
            前に戻る  <
            </span>
          </span>
          <span style="color: #000000; font-weight: 500;">
            <span 
              @click="handleNext" 
              class="next-link"
              style="cursor: pointer; color: #000000; font-weight: 500;"
            >
              > 次に進む
            </span>
          </span>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Loader from "@/components/Loader.vue";
import moment from "moment";
export default {
  name: "JobDescriptionModal",
  components: {
    Loader,
  },
  props: {
    errorMessage: {
      type: String,
      required: false,
    },
    currentJob: {
      type: Object,
      required: true,
    },
    currentIndex: {
      type: Number,
      required: true,
    },
    totalJobs: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    isJobScouted: {
      type: Boolean,
      required: false,
    },
    hasReachedScoutLimit: {
      type: Boolean, 
      required: false,
    },
    company: {
      type: String,
      required: false,
    },
    isInitialScout: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  computed: {
    isScouted() {
      return this.isJobScouted || this.hasReachedScoutLimit || false;
    },
    hasValidJobData() {
      return this.currentJob && 
             this.currentJob.companyName && 
             this.currentJob.job_description;
    },
    validationErrorMessage() {
      if (!this.currentJob) return '求人情報が見つかりません。';
      if (!this.currentJob.companyName) return '企業名の情報が不足しています。';
      if (!this.currentJob.job_description) return '求人詳細の情報が不足しています。';
      return null;
    },
    jobDate() {
      const jobToDisplay = this.currentJob.updated_date || this.currentJob.created_date;
      const date = moment(jobToDisplay).format('YYYY/MM/DD');
      return date;
    },
    scoutHintText(){
      if(this.isJobScouted) {
        return '企業からスカウトが届きやすくなります。';
      } else if(this.hasReachedScoutLimit) {
        return 'スカウト希望は5件まで可能です';
      }
      return '企業からスカウトが届きやすくなります'
    },
    buttonText() {
      if(this.isJobScouted) {
        return 'スカウト希望済'
      } else {
        return 'スカウトを希望する'
      }
    },
    buttonClass() {
      if(this.isJobScouted) {
        return 'scouted-button'
      } else if(this.hasReachedScoutLimit) {
        return 'scout-limit-button'
      } else {
        return 'scout-button'
      }
    }
  },
  methods: {
    show() {
      this.$refs.jobModal.show();
    },
    onHidden() {
      if(this.isInitialScout) this.$emit('close');
    },
    hide() {
      this.$emit('close');
      this.$refs.jobModal.hide();
    },
    handleScout() {
      if (this.isScouted) {
        return;
      }
      this.$emit("scout");
    },
    handleNext() {
      this.$emit("next");
    },
    handlePrev() {
      this.$emit("prev");
    },
    highlightText(text, field) {
      if (!text || !this.currentJob.highlights || !this.currentJob.highlights.length) return text;
      
      const fieldHighlights = this.currentJob.highlights.find(h => h.path === field);
      if (!fieldHighlights || !fieldHighlights.texts) return text;

      // Get hits and sort by length (longest first)
      const hits = fieldHighlights.texts
        .filter(text => text.type === 'hit')
        .map(text => text.value)
        .sort((a, b) => b.length - a.length);

      if (!hits.length) return text;

      // First handle full-width spaces between hits
      let highlightedText = this.highlightFullWidthSpaces(text, hits);
      // Then highlight the hits
      return this.highlightHits(highlightedText, hits);
    },

    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(hit2)})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight">${match}</span>`;
          }
          return match;
        });
      }, text);
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  },
};
</script>
<style scoped>
.scout-button-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 90px;
  /* padding-top: 0px; */
}

.scout-footer {
  display: flex;
  justify-content: center;;
  padding: 10px 0px 0px 0px;
}
.scout-hint {
  text-align: center;
  color: #ff0000;
  font-size: 10px;
  margin-top: 0px;
}
.scouted-hint {
  text-emphasis: center;
  color: #ff0000;
  font-size: 10px;
  margin-top: 0px;
}
.placeholder-height {
  height: 10px;
}
.pad-1 {
  padding: 1rem;
}
.description {
  height: 50vh; 
  overflow-y: auto;
}

/* Responsive height adjustments */
@media (max-width: 768px) {
  .description {
    height: 40vh;
  }
}

@media (min-width: 1200px) {
  .description {
    height: 45vh;
  }
}

@media (min-width: 1600px) {
  .description {
    height: 55vh;
  }
}
.jd-content {
  height: 60vh; /* Match the height of the job content */
  display: flex;
  flex-direction: column;
}

.error-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.error-content {
  text-align: center;
  color: #ff0000;
  font-size: 14px;
  line-height: 1.5;
}

::v-deep .highlight {
  background-color: #ffd700;
  padding: 0 2px;
  border-radius: 2px;
}

::v-deep .job-description-modal .modal-body {
  padding: 0px !important;
}
::v-deep .job-description-modal .modal-footer {
  background: #ffffff !important;
  /* padding-bottom: 0px; */
}

@media (min-width: 1024px) {
  .scout-footer {
    display: flex;
    justify-content: center;
    gap: 50px;
  }
}

.navigation-footer {
  background: #ffffff !important;
  padding: 10px 20px;
  border-top: none !important;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.navigation-toggler {
  display: flex;
  align-items: center;
  gap: 30px;
}

.close-button {
  background: none;
  border: none;
  font-size: 18px;
  color: #000000;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0px !important;
}

.close-button:hover {
  background-color: #e6f3f3;
}

.close-button:active {
  background-color: #d1e7e7;
}

.next-link {
  transition: color 0.2s;
}

.next-link:hover {
  color: #005a5a;
}

/* Mobile responsive styles for scout button section */
@media (max-width: 768px) {
  .scout-button-section {
    width: 90% !important;
    margin: 0 auto;
  }
}
</style>
